import { Link } from '@tanstack/react-router'
import { type StartupMatchData, v2RecordStartupContactInvestor } from '@workspace/api-backend'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import { cn } from '@workspace/ui/lib/utils'
import { AlertTriangle, Check, Clock3, Link2, Loader2, Mic, ScanEye, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useStartupContext } from '../../../contexts/startup-guard'
import { useCreateShareLinkMutation } from '../../../hooks/api'
import { trackEvent } from '../../../lib/gtag'
import {
  EVALUATION_REQUEST_STATUS,
  STRONG_FIT_THRESHOLD,
  TYPICAL_ACTIVITY_RESEARCH_DURATION_S,
  TYPICAL_EVALUATION_DURATION_S,
  TYPICAL_PRESCREEN_DURATION_S,
} from '../../../lib/matches'
import {
  type Contact,
  ContactsGrid,
  PortfolioList,
} from '../../evaluation/components/investor-info-card'
import {
  formatTicket,
  GEO_LABELS,
  INVESTOR_TYPE_LABELS,
  SECTOR_LABELS,
  STAGE_LABELS,
} from '../config/labels'
import { deriveLensGate } from '../lib/lens-gate'
import { InvestorTag } from './investor-tag'

const TAG_LIMIT = 3

function TagGroup({ label, tags }: { label: string; tags: string[] }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? tags : tags.slice(0, TAG_LIMIT)
  const overflow = tags.length - TAG_LIMIT

  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </p>
      <div className="flex flex-wrap gap-1">
        {visible.map((tag) => (
          <InvestorTag key={tag}>{tag}</InvestorTag>
        ))}
        {!expanded && overflow > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setExpanded(true)
            }}
            className="rounded border border-border/50 bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
          >
            +{overflow}
          </button>
        )}
      </div>
    </div>
  )
}

interface RecommendedInvestorCardProps {
  match: StartupMatchData
  startupId: string
  onEvaluate: () => void
  isEvaluating: boolean
  evaluateError?: boolean
  evaluateDisabled?: boolean
}

type ScoreTier = 'strong' | 'neutral'

function tierFor(score: number | null | undefined): ScoreTier | null {
  if (score == null) return null
  return score >= STRONG_FIT_THRESHOLD ? 'strong' : 'neutral'
}

function tierClasses(tier: ScoreTier | null) {
  if (tier === 'strong') {
    return {
      border: 'border-emerald-400/30 bg-emerald-400/5',
      pill: 'bg-emerald-400/10 text-emerald-300',
      pillSub: 'text-emerald-300/70',
      label: 'bg-emerald-400/15 text-emerald-400',
      button:
        'border-emerald-400/40 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20 hover:text-emerald-200',
      scoreText: 'text-emerald-300',
      cta: "They'd take the call \u2192",
    }
  }
  return {
    border: 'border-border/60 bg-card/30',
    pill: 'bg-muted text-muted-foreground',
    pillSub: 'text-muted-foreground/60',
    label: 'bg-muted text-muted-foreground',
    button: '',
    scoreText: 'text-foreground',
    cta: 'See verdict \u2192',
  }
}

export function RecommendedInvestorCard({
  match,
  startupId,
  onEvaluate,
  isEvaluating,
  evaluateDisabled,
}: RecommendedInvestorCardProps) {
  const [open, setOpen] = useState(false)
  const { startup, billing, hasCompletedPitchSession, pitchDeckStatus } = useStartupContext()
  const [elapsed, setElapsed] = useState(0)
  const [progressTransition, setProgressTransition] = useState(false)
  const [investorLinkCopied, setInvestorLinkCopied] = useState(false)
  const createShareLink = useCreateShareLinkMutation(startupId)

  // One-click shortcut, not a settings surface: this link behaves exactly
  // like any other founder-created link (no viaToken/identity association)
  // — it just saves the founder from re-typing the fund's name in Share
  // your Profile.
  const handleCreateInvestorLink = async (e: {
    preventDefault: () => void
    stopPropagation: () => void
  }) => {
    e.preventDefault()
    e.stopPropagation()
    const result = await createShareLink.mutateAsync({
      path: { startupId },
      body: { name: match.investorName },
    })
    const token = result.data?.token
    if (!token) return
    await navigator.clipboard.writeText(`${window.location.origin}/s/${token}`)
    setInvestorLinkCopied(true)
    setTimeout(() => setInvestorLinkCopied(false), 2000)
  }

  const status = match.evaluationRequestStatus
  const isRunning =
    isEvaluating ||
    status === EVALUATION_REQUEST_STATUS.PENDING_PERSONA ||
    status === EVALUATION_REQUEST_STATUS.PENDING_EVALUATION
  const isCompleted = status === EVALUATION_REQUEST_STATUS.COMPLETED && !!match.evaluationId
  const isFailed = status === EVALUATION_REQUEST_STATUS.FAILED
  // The engine sets this error code on prescreen requests that fell back to a
  // stale persona because fresh persona research had FAILED. Surfaces a small
  // "based on older data" badge so the founder knows fidelity is reduced.
  const usedStaleFallback =
    match.evaluationRequestError === 'persona_research_failed_using_stale_fallback'

  const tier = isCompleted ? tierFor(match.evaluationScore) : null
  const t = tierClasses(tier)

  const evaluationsAvailable = billing?.evaluationsAvailable ?? 0
  const canRequestEvaluation = billing?.canRequestEvaluation ?? false
  const { canEvaluate, isPrescreenOnly, isPrescreenRequest, isDeckProcessing } = deriveLensGate({
    hasCompletedPitchSession,
    pitchDeckStatus,
    evaluationRequestKind: match.evaluationRequestKind,
  })
  // Open the dialog whenever the founder can evaluate, even if their wallet
  // can't cover it yet — the dialog links to the wallet top-up page so they
  // never have to leave the investor list.
  const isIdle = !isCompleted && !isRunning && canEvaluate

  const startedAt = match.evaluationRequestCreatedAt
    ? new Date(match.evaluationRequestCreatedAt).getTime()
    : null
  useEffect(() => {
    if (!isRunning) {
      setElapsed(0)
      setProgressTransition(false)
      return
    }
    const base = startedAt ?? Date.now()
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - base) / 1000)))
    tick()
    const id = setInterval(tick, 1000)
    // Enable transition after the initial 0% render has painted to avoid the
    // default block-width (auto/100%) → 0% animation on first appearance.
    const raf = requestAnimationFrame(() => setProgressTransition(true))
    return () => {
      clearInterval(id)
      cancelAnimationFrame(raf)
    }
  }, [isRunning, startedAt])
  const expectedDuration = isPrescreenRequest
    ? TYPICAL_PRESCREEN_DURATION_S
    : TYPICAL_EVALUATION_DURATION_S
  const progressPct = Math.min(95, Math.round((elapsed / expectedDuration) * 100))

  // Whole-row interaction:
  // - completed → entire card is a Link to the evaluation
  // - idle (has credits) → entire card opens the confirmation dialog
  // - everything else → plain div (running keeps its progress UI; no-credits keeps its inner CTA)
  // Outer wrapper holds the border/bg so we can render extra sections
  // (portfolio, contacts) outside the main clickable region without
  // creating invalid nested-interactive HTML.
  const outerClass = cn(
    'rounded-xl border transition-colors',
    isCompleted && t.border,
    !isCompleted && 'border-border/60 bg-card/30'
  )
  const innerClickableClass = cn(
    'block w-full px-5 py-4 text-left transition-colors',
    isCompleted && 'hover:brightness-110 cursor-pointer',
    !isCompleted && isIdle && 'hover:bg-card/50 cursor-pointer'
  )
  const innerStaticClass = 'block w-full px-5 py-4 text-left'

  const geoTags = match.investorGeographies.map((g) => GEO_LABELS[g] ?? g)
  const stageTags = match.investorStages.map((s) => STAGE_LABELS[s] ?? s)
  const sectorTags = match.investorSectors.map((s) => SECTOR_LABELS[s] ?? s)
  const typeTags = match.investorType
    ? [INVESTOR_TYPE_LABELS[match.investorType] ?? match.investorType]
    : []
  const ticketLabel = formatTicket(match.investorMinTicket, match.investorMaxTicket)

  const lastActiveLabel = match.investorLastDealAt ? '< 6 mo' : null

  const hasMetadata =
    geoTags.length > 0 ||
    stageTags.length > 0 ||
    sectorTags.length > 0 ||
    typeTags.length > 0 ||
    !!ticketLabel ||
    !!lastActiveLabel

  const renderInner = (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {match.investorLogoUrl ? (
          <img
            src={match.investorLogoUrl}
            alt={`${match.investorName} logo`}
            className="size-10 shrink-0 rounded-md border border-border/60 bg-card object-cover"
          />
        ) : (
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border/60 bg-card text-xs font-semibold text-muted-foreground">
            {match.investorName
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase() ?? '')
              .join('')}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-foreground">{match.investorName}</h3>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0 gap-1.5"
          onClick={handleCreateInvestorLink}
          disabled={createShareLink.isPending}
        >
          <Link2 className="size-3.5" />
          {investorLinkCopied ? 'Link copied' : 'Create link'}
        </Button>
        <div className="flex shrink-0 items-center gap-2">
          {isCompleted ? (
            <>
              {match.evaluationScore != null && (
                <div className={cn('flex items-baseline gap-0.5 tabular-nums', t.scoreText)}>
                  <span className="text-3xl font-bold leading-none">{match.evaluationScore}</span>
                  <span className="text-xs opacity-50">/100</span>
                </div>
              )}
              <span
                className={cn(
                  'inline-flex h-7 items-center rounded-md border px-3 text-xs font-medium',
                  t.button
                )}
              >
                {t.cta}
              </span>
            </>
          ) : isRunning ? (
            <>
              <div className="flex flex-col items-end gap-1">
                <div className="w-24 overflow-hidden rounded-full bg-muted" style={{ height: 4 }}>
                  <div
                    className={cn(
                      'h-full bg-primary',
                      progressTransition && 'transition-[width] duration-500 ease-linear'
                    )}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {status === 'PENDING_PERSONA'
                    ? 'Preparing…'
                    : isPrescreenRequest
                      ? '~3 min typical'
                      : '~5–10 min typical'}
                </p>
              </div>
              <Button type="button" size="sm" variant="secondary" disabled className="gap-2">
                <Loader2 className="size-3.5 animate-spin" />
                Evaluating
              </Button>
            </>
          ) : isFailed && canEvaluate ? (
            <span className="inline-flex h-8 items-center gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 text-xs font-medium text-amber-200">
              <AlertTriangle className="size-3.5" />
              Retry analysis
            </span>
          ) : isFailed ? (
            <span className="inline-flex h-8 items-center gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 text-xs font-medium text-amber-200">
              <AlertTriangle className="size-3.5" />
              Couldn't analyze
            </span>
          ) : isDeckProcessing && !hasCompletedPitchSession ? (
            <span className="inline-flex h-8 items-center gap-2 rounded-md border border-border/60 bg-muted px-3 text-xs font-medium text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Deck still processing…
            </span>
          ) : !canEvaluate ? (
            <Link
              to="/app/startup/$id/pitch"
              params={{ id: startupId }}
              className={ctaLinkClasses({ size: 'sm' })}
            >
              <Mic />
              <span>Record a pitch first</span>
            </Link>
          ) : evaluateDisabled ? (
            <span className="inline-flex h-8 items-center gap-2 rounded-md border border-border/40 px-3 text-xs font-medium text-muted-foreground/40">
              <ScanEye className="size-3.5" />
              See their take
            </span>
          ) : (
            <span className="inline-flex h-8 items-center gap-2 rounded-md bg-secondary px-3 text-xs font-medium text-secondary-foreground">
              <ScanEye className="size-3.5" />
              See their take
            </span>
          )}
        </div>
      </div>

      {hasMetadata && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-x-6 gap-y-3">
          {stageTags.length > 0 && <TagGroup label="Stage" tags={stageTags} />}
          {geoTags.length > 0 && <TagGroup label="Geography" tags={geoTags} />}
          {sectorTags.length > 0 && <TagGroup label="Sector" tags={sectorTags} />}
          {typeTags.length > 0 && <TagGroup label="Type" tags={typeTags} />}
          {ticketLabel && <TagGroup label="Ticket" tags={[ticketLabel]} />}
          {lastActiveLabel && <TagGroup label="Last investment" tags={[lastActiveLabel]} />}
        </div>
      )}

      {match.reasoning && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            {isCompleted ? "Why they'd write the check" : 'Why we matched them'}
          </p>
          <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-foreground/90">
            {isCompleted && match.reasoning.length > 300
              ? `${match.reasoning.slice(0, 300).trimEnd()}…`
              : match.reasoning}
          </p>
        </div>
      )}

      {match.investorDescription && (
        <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
          {match.investorDescription}
        </p>
      )}

      {isFailed && (
        <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-200/80">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            We couldn't analyze this investor right now.{canEvaluate ? ' Tap to try again.' : ''}
          </span>
        </p>
      )}

      {usedStaleFallback && !isFailed && (
        <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-200/80">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>Based on earlier research, so accuracy may be reduced.</span>
        </p>
      )}
    </div>
  )

  const dialog = isIdle ? (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            {match.investorLogoUrl ? (
              <img
                src={match.investorLogoUrl}
                alt=""
                className="size-12 shrink-0 rounded-lg border border-border/60 bg-card object-cover"
              />
            ) : (
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-card text-sm font-semibold text-muted-foreground">
                {match.investorName
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0]?.toUpperCase() ?? '')
                  .join('')}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                See their take
              </p>
              <DialogTitle className="truncate text-xl leading-tight">
                {match.investorName}
              </DialogTitle>
            </div>
          </div>
          {isPrescreenOnly && (
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-400/30 bg-amber-400/5 px-3 py-2.5 text-xs leading-relaxed text-amber-200">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                Heads up: accuracy may be limited since this is based on your deck only. Record an
                AI Session for a sharper, more reliable read.
              </span>
            </div>
          )}
          <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
            {isPrescreenOnly
              ? 'We rebuild their decision lens from their thesis, portfolio, and public writing, then run your pitch deck through it for a directional read on fit.'
              : "Before you spend a warm intro, see what they'd likely say in their Monday partner meeting. We rebuild their decision lens from their thesis, portfolio, and public writing, then run your most recent pitch through it."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            What you'll get back
          </p>
          <ul className="space-y-2.5">
            {(isPrescreenOnly
              ? [
                  'A directional read on fit \u2014 not a pitch critique',
                  "Where you fit their thesis (and where you don't)",
                ]
              : [
                  'A read on whether they\u2019d take the call',
                  "Where you fit their thesis (and where you don't)",
                  "The questions they'd likely hit you with first",
                  'What to change in your deck before you reach out',
                ]
            ).map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                <Check className="mt-0.5 size-4 shrink-0 text-emerald-400" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-4 rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-xs">
          <div className="flex items-center gap-2">
            <Zap className="size-4 text-foreground/70" />
            <div className="leading-tight">
              <div className="font-semibold text-foreground">1 evaluation</div>
              <div className="text-[10px] text-muted-foreground">
                {evaluationsAvailable > 0 ? `${evaluationsAvailable} prepaid` : '$2 per evaluation'}
              </div>
            </div>
          </div>
          <div className="h-8 w-px bg-border" />
          <div className="flex items-center gap-2">
            <Clock3 className="size-4 text-foreground/70" />
            <div className="leading-tight">
              <div className="font-semibold text-foreground">
                {isPrescreenOnly ? '~3 minutes' : '5–10 minutes'}
              </div>
              <div className="text-[10px] text-muted-foreground">
                Leave the page and we'll save it
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2 sm:flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setOpen(false)
              trackEvent('see_their_take_dismissed', {
                startup_id: startupId,
                investor_id: match.investorId,
              })
            }}
            className="sm:mr-auto"
          >
            Not now
          </Button>
          {isPrescreenOnly && (
            <Button
              asChild
              type="button"
              variant="outline"
              onClick={() =>
                trackEvent('see_their_take_record_session_clicked', {
                  startup_id: startupId,
                  investor_id: match.investorId,
                })
              }
            >
              <Link to="/app/startup/$id/pitch" params={{ id: startupId }}>
                Record AI session
              </Link>
            </Button>
          )}
          {canRequestEvaluation ? (
            <Button
              type="button"
              onClick={() => {
                setOpen(false)
                trackEvent('see_their_take_confirmed', {
                  startup_id: startupId,
                  investor_id: match.investorId,
                  variant: 'evaluate',
                })
                onEvaluate()
              }}
            >
              See their take →
            </Button>
          ) : (
            <Button
              asChild
              type="button"
              onClick={() =>
                trackEvent('see_their_take_confirmed', {
                  startup_id: startupId,
                  investor_id: match.investorId,
                  variant: 'buy',
                })
              }
            >
              <Link to="/app/startup/$id/billing" params={{ id: startupId }}>
                See their take →
              </Link>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null

  // Shared by the card's click and keyboard handlers so the two cannot drift.
  const openTake = () => {
    setOpen(true)
    trackEvent('see_their_take_modal_opened', {
      startup_id: startupId,
      investor_id: match.investorId,
      is_prescreen_only: isPrescreenOnly,
    })
  }

  const mainArea = isCompleted ? (
    <Link
      to="/app/startup/$id/evaluation/$evaluationId"
      params={{
        id: startupId,
        evaluationId: match.evaluationId as string,
      }}
      className={innerClickableClass}
      aria-label={`See ${match.investorName} verdict${
        match.evaluationScore != null ? `, ${match.evaluationScore} out of 100` : ''
      }`}
    >
      {renderInner}
    </Link>
  ) : isIdle && !evaluateDisabled ? (
    // The card body holds block-level content AND its own interactive element
    // (the tag list's "+N" expander), and a <button> may legally contain
    // neither — React warns about the nesting on every render of this page.
    //
    // So the button is an overlay rather than a wrapper: a real <button> that
    // covers the card and owns the click, with the content layered above it and
    // transparent to pointer events. Clicks on plain content fall through to the
    // overlay; the "+N" expander and any links re-enable pointer events and keep
    // their own. Keyboard and screen-reader behaviour stay native to <button>.
    <div className={cn(innerClickableClass, 'relative')}>
      <button
        type="button"
        onClick={openTake}
        className="absolute inset-0 cursor-pointer rounded-none"
        aria-label={`Pre-pitch ${match.investorName}`}
      />
      <div className="pointer-events-none relative [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
        {renderInner}
      </div>
    </div>
  ) : isIdle ? (
    <div className={innerStaticClass}>{renderInner}</div>
  ) : (
    <div className={innerStaticClass}>{renderInner}</div>
  )

  return (
    <>
      <div className={outerClass}>
        {mainArea}
        <InvestorExtras
          match={match}
          startupId={startupId}
          startupName={startup.name}
          startupDescription={startup.description ?? null}
        />
      </div>
      {dialog}
    </>
  )
}

// ─── Portfolio + contacts (mirrors evaluation page InvestorInfoCard) ────────

interface InvestorExtrasProps {
  match: StartupMatchData
  startupId: string
  startupName: string
  startupDescription: string | null
}

function InvestorExtras({
  match,
  startupId,
  startupName: _startupName,
  startupDescription: _startupDescription,
}: InvestorExtrasProps) {
  const portfolios = match.investorRecentPortfolios ?? []
  const contacts = match.investorContacts ?? []
  // Show "Preparing contacts…" only while activity research is actually
  // in-flight. Once it completes (with 0 or N contacts) the spinner goes away;
  // an investor that legitimately has no public contacts shouldn't spin
  // forever.
  const showContactsPreparing = contacts.length === 0 && match.investorContactsResearchInProgress
  const hasAny = portfolios.length > 0 || contacts.length > 0 || showContactsPreparing
  if (!hasAny) return null

  const handleContactClick = (contact: Contact) => {
    trackEvent('investor_linkedin_opened', {
      startup_id: startupId,
      investor_id: match.investorId,
      contact_id: contact.id,
    })
    void v2RecordStartupContactInvestor({
      path: { startupId, investorId: match.investorId },
    }).catch(() => {})
    const url = contact.linkedinUrl ?? match.investorWebsiteUrl
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="space-y-3 border-t border-border/40 px-5 py-4">
      {portfolios.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Portfolio
          </p>
          <PortfolioList companies={portfolios} />
        </div>
      )}

      {contacts.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Contacts
          </p>
          <ContactsGrid
            contacts={contacts}
            websiteUrl={match.investorWebsiteUrl}
            onContact={handleContactClick}
          />
        </div>
      )}

      {showContactsPreparing && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Contacts
          </p>
          <div className="flex items-center gap-2 rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" aria-hidden="true" />
            <span>
              Preparing contacts, typically ~{Math.ceil(TYPICAL_ACTIVITY_RESEARCH_DURATION_S / 60)}{' '}
              min. Updates in the background.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
